import { useAsyncOperation } from "@core/hooks/use-async-operation";
import { useI18n } from "@core/i18n/i18n";
import { healthcareTeamManager, networkActionsErrorsManager } from "@core/service-container";
import { RegisteredPtInviteData, UnregisteredPtInviteData } from "@domain/healthcare-team/healthcare-team";
import {
	InvitationPtAlreadyInHctError,
	InvitationPtInDifferentCountryError,
	InvitationPtNotFoundError,
} from "@domain/healthcare-team/healthcare-team-errors";
import { Patient } from "@domain/patient/patient";

import styled from "@emotion/styled";
import { Button } from "@ui/components/buttons/button";
import { BaseDialog } from "@ui/components/dialog/base-dialog";
import { ErrorMessage } from "@ui/components/common/error-message";
import { stack } from "@ui/style";
import { isNotNil } from "@utils/assert";
import { REGEX_EMAIL } from "@utils/regex-utils";
import { DialogProps } from "primereact/dialog";
import { InputText } from "primereact/inputtext";
import React, { useState } from "react";
import { Controller, useForm } from "react-hook-form";

type EmailFormData = {
	email: string;
};

interface EmailFormDialogProps extends DialogProps {
	patient: Patient;
	defaultValues?: EmailFormData;
	onRegisteredPt: (ptData: RegisteredPtInviteData) => void;
	onUnregisteredPt: (ptDate: UnregisteredPtInviteData) => void;
}

export const EmailFormDialog = ({
	patient,
	defaultValues = {
		email: "",
	},
	onRegisteredPt,
	onUnregisteredPt,
	...otherProps
}: EmailFormDialogProps): JSX.Element => {
	const { format } = useI18n();
	const [emailError, setEmailError] = useState<string>();

	const { control, handleSubmit } = useForm<EmailFormData>({
		defaultValues,
	});

	const [submit, isLoading] = useAsyncOperation(async (data: EmailFormData) => {
		try {
			const invitedPt = await healthcareTeamManager.checkEmailRegistered(patient.id, data.email);
			onRegisteredPt(invitedPt);
		} catch (e) {
			if (e instanceof InvitationPtNotFoundError) {
				try {
					const invitedPt = await healthcareTeamManager.sendUnregisteredInvitation(patient.id, data.email);
					onUnregisteredPt(invitedPt);
					return;
				} catch (e2) {
					networkActionsErrorsManager.reportError(e2);
				}
			}
			if (e instanceof InvitationPtInDifferentCountryError) {
				setEmailError(format("patientDetails.healthcareTeam.inviteDialog.form.email.ptInDifferentCountryError"));
			} else if (e instanceof InvitationPtAlreadyInHctError) {
				setEmailError(format("patientDetails.healthcareTeam.inviteDialog.form.email.alreadyJoinedError"));
			} else {
				console.error("Failed to get PT data required for invitation", e);
				networkActionsErrorsManager.reportError(e);
			}
		}
	}, {});

	return (
		<StyledDialog
			header={format("patientDetails.healthcareTeam.inviteDialog.header")}
			footer={
				<ButtonContainer>
					<Button
						loading={isLoading}
						type="submit"
						onClick={handleSubmit(submit)}
						label={format("global.dialog.next")}
					/>
				</ButtonContainer>
			}
			{...otherProps}
		>
			<Form onSubmit={handleSubmit(submit)}>
				<FormField>
					<label className="p-size-m">{format("patientDetails.healthcareTeam.inviteDialog.form.email.label")}</label>
					<Controller
						name="email"
						control={control}
						rules={{
							onChange: () => setEmailError(undefined),
							required: format("global.form.field.requiredError"),
							pattern: {
								value: REGEX_EMAIL,
								message: format("patientDetails.healthcareTeam.inviteDialog.form.email.formatError"),
							},
						}}
						render={({ field, fieldState }) => (
							<>
								<EmailInputText
									onChange={field.onChange}
									value={field.value}
									className={isNotNil(fieldState.error) || isNotNil(emailError) ? "p-invalid" : undefined}
									placeholder={format("patientDetails.healthcareTeam.inviteDialog.form.email.placeholder")}
								/>
								<ErrorMessage>{fieldState.error?.message ?? emailError}</ErrorMessage>
							</>
						)}
					/>
				</FormField>
			</Form>
		</StyledDialog>
	);
};

const StyledDialog = styled(BaseDialog)`
	width: 732px;
`;

const ButtonContainer = styled.div`
	place-self: end;
`;

const EmailInputText = styled(InputText)``;

const Form = styled.form`
	${stack("M", "spacebetween", "stretch")};
	height: 100%;
`;

const FormField = styled.div`
	${stack("S", "flexstart", "stretch")};
`;
